import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'

import type { TaskTreeNode } from '../Contracts/Task.types'

/**
 * Форми відповіді `GET .../tree`. Бек віддає ПЛАСКИЙ масив вузлів (SPEC-003 §5.7);
 * `{items}`/`hydra:member` — захисні гілки на випадок обгортки (як у History-стора).
 */
type TreeResponse = { items?: TaskTreeNode[]; 'hydra:member'?: TaskTreeNode[] } | TaskTreeNode[] | null

const extractNodes = (data: TreeResponse): TaskTreeNode[] => {
    if (Array.isArray(data)) return data
    const items = data?.items ?? data?.['hydra:member']
    return Array.isArray(items) ? items : []
}

/**
 * Локальний стор вкладки «Дерево» (SPEC-008 §5.4).
 *
 * Тримає плаский масив вузлів дерева та прапорець запиту. Інстанс — per-mount
 * (`useState(() => new TaskTreeTabStore())`), як `TaskHistoryTabStore`. Дерево
 * будує вже серверний `GET /services/tasks/{uuid}/tree` (N10) — на відміну від
 * легасі, що тягнув усю колекцію і рахував ієрархію в браузері.
 */
export class TaskTreeTabStore {
    nodes: TaskTreeNode[] = []
    loading = false

    constructor() {
        makeAutoObservable(this)
    }

    async load(uuid: string | undefined): Promise<void> {
        if (!uuid) return
        this.loading = true
        try {
            // `restClient.get` рантаймно повертає розпарсений JSON; тип — ky
            // `ResponsePromise`, тож звужуємо через `unknown` (конвенція репо).
            // forceRefresh: обходимо 30с GET-дедуплікацію ядра — статуси вузлів мають бути свіжі.
            const raw = await restClient.get(`/services/tasks/${uuid}/tree`, undefined, { forceRefresh: true })
            const data = raw as unknown as TreeResponse
            const nodes = extractNodes(data)
            runInAction(() => {
                this.nodes = nodes
            })
        } catch {
            runInAction(() => {
                this.nodes = []
            })
        } finally {
            runInAction(() => {
                this.loading = false
            })
        }
    }
}
